import { useState, useMemo } from "react";
import { MdLockOutline, MdMailOutline, MdVpnKey } from "react-icons/md";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import Form from "../forms/Form";
import { Link, useNavigate } from "react-router-dom";
import AuthTextBox from "./inputs/AuthTextBox";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import {
  postGoogleLogin,
  postMicrosoftLogin
} from "../../utils/api/endpoints/user/authentication";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../utils/auth/authConfig";
import toast from "react-hot-toast";
import { useStoreDispatch } from "../../utils/hooks/hooks";
import { setCredentials } from "../../utils/store/reducer/slices/userReducer";
import { createPortal } from "react-dom";
import { initiateSSOLogin } from "../../utils/api/endpoints/sso";

/**
 * Allow the user to enter their login credentials and get authenticated.
 * Also lets them enter 2FA codes if applicable.
 */
const LoginForm = () => {
  const { handleLogin } = useUserCredentials();
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const { instance } = useMsal();

  const [showSSOModal, setShowSSOModal] = useState(false);
  const [ssoEmailInput, setSsoEmailInput] = useState("");
  const [ssoLoading, setSsoLoading] = useState(false);

  const handleSSORedirect = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = ssoEmailInput.trim();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid work email address.");
      return;
    }

    setSsoLoading(true);
    try {
      const res = await initiateSSOLogin(email);
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to initiate SSO.");
    } finally {
      setSsoLoading(false);
    }
  };

  const defaultValues = useMemo(
    () => ({
      email: "",
      password: ""
    }),
    []
  );

  const handleGoogleSuccess = async (credentialResponse: any) => {
    const idToken = credentialResponse.credential as string | undefined;
    if (idToken) {
      try {
        const credentials = await postGoogleLogin(idToken);
        if (credentials) {
          // Dispatch to Redux store first
          dispatch(setCredentials(credentials));

          // Role-based redirection
          const dashboardPath = getDashboardPathByRole(credentials.userRole);
          requestAnimationFrame(() => {
            navigate(dashboardPath, { replace: true });
          });
        }
      } catch (error: any) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        if (status === 404) {
          // Not registered → show generic signup message
          toast.error(
            message || "No account found. Please sign up before signing in."
          );
        } else if (status === 409) {
          toast.error(
            message ||
            "This email is associated with a different role. Use your original login method."
          );
        } else {
          console.error("Google login error:", error);
          toast.error("Google login failed. Please try again.");
        }
      }
    }
  };

  const handleGoogleError = () => {
    console.error("Google login failed");
  };

  const handleMicrosoftLogin = async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      const idToken = response.idToken;

      if (idToken) {
        const credentials = await postMicrosoftLogin(idToken);
        if (credentials) {
          dispatch(setCredentials(credentials));

          // Role-based redirection
          const dashboardPath = getDashboardPathByRole(credentials.userRole);
          requestAnimationFrame(() => {
            navigate(dashboardPath, { replace: true });
          });
        }
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 404) {
        toast.error(
          message || "No account found. Please sign up before signing in."
        );
      } else if (status === 409) {
        toast.error(
          message ||
          "This email is associated with a different role. Use your original login method."
        );
      } else {
        console.error("Microsoft login error:", error);
        toast.error("Microsoft login failed. Please try again.");
      }
    }
  };

  // Helper function to determine dashboard path based on user role
  const getDashboardPathByRole = (role: UserRole | undefined) => {
    if (!role) return "/dashboard"; // Default to business dashboard if no role

    switch (role) {
      case UserRole.ADMIN:
      case UserRole.ADMIN_MANAGER:
      case UserRole.SUB_ADMIN:
        return "/admin/dashboard";
      case UserRole.RESEARCHER:
      case UserRole.QA:
      case UserRole.DEVELOPER:
        return "/dashboard"; // Researcher dashboard route
      case UserRole.BUSINESS:
      case UserRole.BUSINESS_MANAGER:
      case UserRole.BUSINESS_ADMINISTRATOR:
        return "/dashboard"; // Business dashboard
      default:
        return "/dashboard"; // Default to business dashboard
    }
  };

  return (
    <article className="mx-auto w-full max-w-2xl">


      {/* SSO Buttons */}
      <div className="mb-6 space-y-3">
        {/* Google SSO */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <button
            type="button"
            onClick={() => {
              // Trigger Google login by clicking the hidden Google button
              const googleBtn = document.querySelector('[aria-labelledby="button-label"]') as HTMLElement;
              googleBtn?.click();
            }}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
              <path fill="#34A853" d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.909-2.258c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" />
              <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9.001c0 1.452.348 2.827.957 4.041l3.007-2.332z" />
              <path fill="#EA4335" d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" />
            </svg>
            <span>Sign in with Google</span>
          </button>
        )}

        {/* Hidden Google Login for actual authentication */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <div className="hidden">
            <GoogleOAuthProvider
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
            >
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />
            </GoogleOAuthProvider>
          </div>
        )}

        {/* Microsoft SSO */}
        {process.env.REACT_APP_MICROSOFT_CLIENT_ID && (
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path d="M9.5 1H1V9.5H9.5V1Z" fill="#F25022" />
              <path d="M10.5 1H19V9.5H10.5V1Z" fill="#7FBA00" />
              <path d="M9.5 10.5H1V19H9.5V10.5Z" fill="#00A4EF" />
              <path d="M10.5 10.5H19V19H10.5V10.5Z" fill="#FFB900" />
            </svg>
            <span>Sign in with Microsoft</span>
          </button>
        )}

        {/* Enterprise SSO */}
        <button
          type="button"
          onClick={() => setShowSSOModal(true)}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0B1731] focus:ring-offset-2"
        >
          <MdVpnKey className="w-5 h-5 text-[#0B1731]" />
          <span>Sign in with Enterprise SSO</span>
        </button>

        {/* SSO Email Modal */}
        {showSSOModal &&
          createPortal(
            <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
              <div className="w-full max-w-md bg-white rounded-xl p-6 shadow-2xl border border-gray-100 relative">
                <h3
                  className="text-lg text-[#010D2C] mb-1"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}
                >
                  Single Sign-On (SSO)
                </h3>
                <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                  Enter your work email address. If your company uses Okta or Single Sign-On, you will be redirected to your identity provider.
                </p>
                <form onSubmit={handleSSORedirect} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Work Email
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="you@company.com"
                      value={ssoEmailInput}
                      onChange={e => setSsoEmailInput(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0B1731]"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowSSOModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={ssoLoading}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-[#0B1731] hover:bg-[#0e1d40] rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B1731] disabled:opacity-50"
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {ssoLoading ? "Connecting..." : "Continue to SSO"}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

        {/* Simple Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-4 text-gray-500">Or</span>
          </div>
        </div>
      </div>

      <Form
        defaultValues={defaultValues}
        onSubmit={handleLogin}
        className="space-y-6"
      >
        <AuthTextBox
          name="email"
          label="Email"
          placeholder="Enter your email address"
          rules={{
            required: true
          }}
          Icon={MdMailOutline}
        />

        <AuthTextBox
          name="password"
          label="Password"
          password={true}
          placeholder="Enter your password"
          rules={{
            required: true
          }}
          Icon={MdLockOutline}
        />

        <div className="flex items-center justify-end">
          <Link
            to="/reset-password"
            className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full rounded-md bg-[#0B1731] px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0e1d40] focus:outline-none focus:ring-2 focus:ring-[#0B1731] focus:ring-offset-2"
        >
          Sign in
        </button>
      </Form>
      {/* Footer */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </article>
  );
};

export default LoginForm;
